export function DocsSelfHostingPage() {
  return (
    <div className="docs-article">
      <p className="docs-eyebrow">Deploy</p>
      <h1 className="docs-page-title">Self-hosting</h1>
      <p className="docs-lede">
        Woven is self-hostable as the <code>woven-server</code> executable. Use the loopback
        development composition for local work, or opt into static remote native QUIC or managed
        QUIC with optional browser WebTransport, using your own TLS and credential files.
      </p>

      <h2 className="docs-heading">Install</h2>
      <p className="docs-body">Install the published server executable directly with Cargo:</p>
      <pre className="code-block">
        <code>cargo install woven-server --locked</code>
      </pre>
      <p className="docs-body">
        Cargo installs <code>woven-server</code> on your executable path. Start the local
        development composition with:
      </p>
      <pre className="code-block">
        <code>woven-server</code>
      </pre>
      <p className="docs-body">
        The default development composition binds to loopback and uses development credentials with
        ephemeral TLS. Keep that composition on the local machine.
      </p>

      <h2 className="docs-heading">Run from current source</h2>
      <pre className="code-block">
        <code>cargo run -p woven-server</code>
      </pre>
      <p className="docs-body">
        Run this command from a Woven source checkout when integrating against the current source
        tree rather than the published Cargo package.
      </p>

      <h2 className="docs-heading">Remote native QUIC</h2>
      <pre className="code-block">
        <code>{`WOVEN_REMOTE_QUIC=1 \\
WOVEN_QUIC_BIND=0.0.0.0:8081 \\
WOVEN_MANAGEMENT_BIND=127.0.0.1:8080 \\
WOVEN_TLS_CERT_FILE=/run/woven/tls/chain.pem \\
WOVEN_TLS_KEY_FILE=/run/woven/tls/key.pem \\
WOVEN_AUTH_TOKEN_FILE=/run/woven/auth/token \\
woven-server`}</code>
      </pre>
      <p className="docs-body">
        All six settings are required together and partial configuration fails closed. Supply a PEM
        certificate chain and matching private key. The auth-token file contains the static scoped
        client credential; keep the key and token files private and pass file paths, never secret
        values, through the environment.
      </p>
      <div className="panel notice-panel docs-spatial-notice">
        <p className="page-prose">
          <strong>Keep management on loopback.</strong> <code>WOVEN_MANAGEMENT_BIND</code> exposes
          unauthenticated <code>/healthz</code>, <code>/readyz</code>, <code>/metrics</code>, and
          <code>/v1/capabilities</code> for local operations. Do not publish or proxy that listener.
        </p>
      </div>

      <h2 className="docs-heading">Host-managed QUIC and optional WebTransport</h2>
      <p className="docs-body">
        The managed composition starts empty; Woven Host adds session scopes and client credentials.
        The following settings enable native QUIC; browser WebTransport is a separate optional
        listener:
      </p>
      <pre className="code-block">
        <code>{`WOVEN_MANAGED_QUIC=1
WOVEN_QUIC_BIND=0.0.0.0:8081
WOVEN_MANAGEMENT_BIND=127.0.0.1:8080
WOVEN_ADMIN_BIND=127.0.0.1:8082
WOVEN_TLS_CERT_FILE=/run/woven/tls/chain.pem
WOVEN_TLS_KEY_FILE=/run/woven/tls/key.pem
WOVEN_ADMIN_TOKEN_FILE=/run/woven/admin/token`}</code>
      </pre>
      <p className="docs-body">
        Both HTTP listeners must remain loopback-only. The separate admin listener requires its own
        bearer credential on every route; point Woven Host's <code>WOVEN_MANAGEMENT_URL</code> at
        that listener and provide the matching credential through Host's
        <code>WOVEN_MANAGEMENT_TOKEN_FILE</code>. Host provisions bounded session scopes while
        clients connect directly to Woven. To enable browser WebTransport, also set
        <code> WOVEN_MANAGED_WEBTRANSPORT=1</code>, an explicit
        <code> WOVEN_WEBTRANSPORT_BIND</code>, an absolute
        <code> WOVEN_WEBTRANSPORT_PATH</code>, and exact canonical origins in
        <code> WOVEN_WEBTRANSPORT_ALLOWED_ORIGINS</code>. All four settings are required together;
        the WebTransport UDP listener must be separate from native QUIC. Configure Host&apos;s
        <code> WOVEN_CLIENT_WEBTRANSPORT_URL</code> separately, and use the revealed
        descriptor&apos;s
        <code> webTransport.url</code> and browser trust metadata when connecting.
      </p>

      <h2 className="docs-heading">Health and metrics</h2>
      <p className="docs-body">
        <code>/readyz</code> reports whether native QUIC is active. <code>/metrics</code> uses
        Prometheus text exposition and includes active connection/session gauges plus
        process-lifetime counters for connections, publishes, delivered events and bytes, queue
        drops, and evictions/coalescing. Keep these operational endpoints private and scrape them
        over a trusted local or private path.
      </p>
      <p className="docs-body">
        Static remote mode remains native QUIC only; managed mode can additionally expose browser
        WebTransport. Certificate issuance, renewal, and perimeter controls remain the
        operator&apos;s responsibility. Durable journaling is an external integration seam, not a
        built-in restart-recovery guarantee, and separate nodes do not automatically share sessions
        or state.
      </p>
    </div>
  );
}
